import React, { useState, useEffect, useCallback } from 'react';
import rbacService from '../../services/rbacService';
import VisualSelector from '../../components/common/VisualSelector';
import { toast } from 'react-toastify';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import useDebounce from '../../hooks/useDebounce';
import { useTranslation } from 'react-i18next';
import './AdminAccessControl.css';

const AdminAccessControl = () => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState('groups');
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 500);
    const [sortConfig, setSortConfig] = useState({
        groups: { by: 'name', order: 'ASC' },
        permissions: { by: 'slug', order: 'ASC' },
        roles: { by: 'name', order: 'ASC' },
        routes: { by: 'path', order: 'ASC' }
    });

    const [pagination, setPagination] = useState({
        groups: { items: [], currentPage: 1, pages: 1 },
        permissions: { items: [], currentPage: 1, pages: 1 },
        routes: { items: [], currentPage: 1, pages: 1 },
        roles: { items: [], currentPage: 1, pages: 1 }
    });
    const [allPermissions, setAllPermissions] = useState([]);
    const [allRoles, setAllRoles] = useState([]);
    const [allGroups, setAllGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedItem, setSelectedItem] = useState(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [confirmModal, setConfirmModal] = useState({ isOpen: false });

    // Form States for details/editing
    const [editForm, setEditForm] = useState({
        group: { name: '', description: '', permissionIds: [], roles: [] },
        permission: { slug: '', description: '' },
        role: { name: '', description: '', groups: [], directPermissionIds: [] },
        route: { path: '', method: 'GET', permissions: [], is_public: false }
    });

    const loadTabData = useCallback(async (tab, page = 1, search = '') => {
        setLoading(true);
        try {
            let result;
            const { by, order } = sortConfig[tab] || { by: 'createdAt', order: 'DESC' };
            console.log(`[RBAC UI] Loading ${tab}, Page: ${page}, Search: "${search}", SortBy: ${by}, Order: ${order}`);

            if (tab === 'groups') {
                result = await rbacService.getGroups(page, 20, search, by, order);
                const allR = await rbacService.getRoles();
                setAllRoles(allR.items || allR);
            }
            else if (tab === 'permissions') result = await rbacService.getPermissions(page, 20, search, by, order);
            else if (tab === 'roles') {
                result = await rbacService.getRoles(page, 20, search, by, order);
                const allG = await rbacService.getGroups();
                setAllGroups(allG.items || allG);
            }
            else if (tab === 'routes') {
                const items = await rbacService.getRouteConfigs();
                // Route configs are client-side sorted for now as it's a small list
                const sorted = [...items].sort((a, b) => {
                    const factor = order === 'ASC' ? 1 : -1;
                    return (a[by] > b[by] ? 1 : -1) * factor;
                });
                result = { items: sorted };
            }

            setPagination(prev => ({
                ...prev,
                [tab]: {
                    items: result.items || result,
                    currentPage: result.currentPage || 1,
                    pages: result.pages || 1
                }
            }));

            if (allPermissions.length === 0) {
                const perms = await rbacService.getPermissions();
                setAllPermissions(Array.isArray(perms) ? perms : perms.items || []);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [allPermissions.length, sortConfig]);

    useEffect(() => {
        loadTabData(activeTab, 1, debouncedSearch);
    }, [activeTab, debouncedSearch, sortConfig, loadTabData]);

    const handleSort = (field) => {
        setSortConfig(prev => {
            const current = prev[activeTab];
            const nextOrder = (current.by === field && current.order === 'ASC') ? 'DESC' : 'ASC';
            return {
                ...prev,
                [activeTab]: { by: field, order: nextOrder }
            };
        });
    };

    const handleSelectItem = (item) => {
        setSelectedItem(item);
        setShowCreateForm(false);
        if (activeTab === 'groups') {
            setEditForm({
                ...editForm,
                group: {
                    name: item.name,
                    description: item.description,
                    permissionIds: item.permissions?.map(p => p.id) || [],
                    roles: item.roles?.map(r => ({ roleId: r.id, isDefault: r.RoleGroup?.is_default })) || []
                }
            });
        } else if (activeTab === 'permissions') {
            setEditForm({ ...editForm, permission: { slug: item.slug, description: item.description } });
        } else if (activeTab === 'roles') {
            setEditForm({
                ...editForm,
                role: {
                    name: item.name,
                    description: item.description,
                    groups: item.groups?.map(g => ({ groupId: g.id, isDefault: g.RoleGroup?.is_default })) || [],
                    directPermissionIds: item.direct_permissions?.map(p => p.id) || []
                }
            });
        } else if (activeTab === 'routes') {
            setEditForm(f => ({
                ...f,
                route: {
                    path: item.path,
                    method: item.method,
                    permissions: item.permissions || [],
                    description: item.description || '',
                    is_public: item.is_public || false
                }
            }));
        }
    };

    const handleOpenCreate = () => {
        setSelectedItem(null);
        setShowCreateForm(true);
        setEditForm({
            group: { name: '', description: '', permissionIds: [], roles: [] },
            permission: { slug: '', description: '' },
            route: { path: '', method: 'GET', permissions: [], is_public: false },
            role: { name: '', description: '', groups: [], directPermissionIds: [] }
        });
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            if (selectedItem) {
                // Update
                if (activeTab === 'groups') {
                    await rbacService.updateGroup(selectedItem.id, { name: editForm.group.name, description: editForm.group.description });
                    await rbacService.syncPermissionsForGroup(selectedItem.id, editForm.group.permissionIds);
                    await rbacService.syncRolesForGroup(selectedItem.id, editForm.group.roles);
                } else if (activeTab === 'permissions') {
                    await rbacService.updatePermission(selectedItem.id, editForm.permission);
                } else if (activeTab === 'roles') {
                    await rbacService.updateRole(selectedItem.id, { name: editForm.role.name, description: editForm.role.description });
                    await rbacService.syncGroupsForRole(selectedItem.id, editForm.role.groups);
                    await rbacService.syncPermissionsForRole(selectedItem.id, editForm.role.directPermissionIds);
                } else if (activeTab === 'routes') {
                    await rbacService.updateRouteConfig(selectedItem.id, editForm.route);
                }
            } else {
                // Create
                if (activeTab === 'groups') {
                    const newGrp = await rbacService.createGroup(editForm.group.name, editForm.group.description);
                    if (editForm.group.permissionIds.length > 0) {
                        await rbacService.syncPermissionsForGroup(newGrp.id, editForm.group.permissionIds);
                    }
                    if (editForm.group.roles.length > 0) {
                        await rbacService.syncRolesForGroup(newGrp.id, editForm.group.roles);
                    }
                } else if (activeTab === 'permissions') {
                    await rbacService.createPermission(editForm.permission.slug, editForm.permission.description);
                } else if (activeTab === 'roles') {
                    const newRole = await rbacService.createRole({ name: editForm.role.name, description: editForm.role.description });
                    if (editForm.role.groups.length > 0) {
                        await rbacService.syncGroupsForRole(newRole.id, editForm.role.groups);
                    }
                } else if (activeTab === 'routes') {
                    await rbacService.createRouteConfig(editForm.route);
                }
            }
            setSelectedItem(null);
            setShowCreateForm(false);
            loadTabData(activeTab, pagination[activeTab].currentPage, searchTerm);
            toast.success(t('operation_success'));
        } catch (e) {
            toast.error(t('operation_failed'));
        }
    };

    const handleDelete = () => {
        setConfirmModal({ isOpen: true });
    };

    const confirmDeleteAction = async () => {
        try {
            if (activeTab === 'groups') await rbacService.deleteGroup(selectedItem.id);
            else if (activeTab === 'permissions') await rbacService.deletePermission(selectedItem.id);
            else if (activeTab === 'roles') await rbacService.deleteRole(selectedItem.id);
            else if (activeTab === 'routes') await rbacService.deleteRouteConfig(selectedItem.id);
            setSelectedItem(null);
            loadTabData(activeTab, pagination[activeTab].currentPage, searchTerm);
            toast.success(t('delete_success'));
        } catch (e) {
            toast.error(t('delete_failed'));
        }
        setConfirmModal({ isOpen: false });
    };

    const renderSortArrow = (field) => {
        const config = sortConfig[activeTab];
        if (config.by !== field) return <span className="sort-placeholder">↕</span>;
        return config.order === 'ASC' ? ' ↑' : ' ↓';
    };

    return (
        <div className="admin-access-control-v2">
            <div className="master-list-panel">
                <div className="panel-header">
                    <div className="tab-switcher">
                        <button className={activeTab === 'groups' ? 'active' : ''} onClick={() => setActiveTab('groups')}>{t('groups')}</button>
                        <button className={activeTab === 'permissions' ? 'active' : ''} onClick={() => setActiveTab('permissions')}>{t('permissions')}</button>
                        <button className={activeTab === 'routes' ? 'active' : ''} onClick={() => setActiveTab('routes')}>{t('routes')}</button>
                        <button className={activeTab === 'roles' ? 'active' : ''} onClick={() => setActiveTab('roles')}>{t('roles')}</button>
                    </div>
                    <div className="panel-actions">
                        <div className="search-box">
                            <input
                                type="text" placeholder={t('filter_placeholder', { item: t(activeTab) })}
                                value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <button className="btn-create" onClick={handleOpenCreate}>{t('new_item')}</button>
                    </div>
                </div>

                <div className="list-content">
                    {loading && <div className="loader">Loading...</div>}
                    <table className="modern-list-table">
                        <thead>
                            <tr>
                                <th onClick={() => handleSort(activeTab === 'permissions' ? 'slug' : (activeTab === 'routes' ? 'path' : 'name'))} className="sortable">
                                    {t('name_slug')} {renderSortArrow(activeTab === 'permissions' ? 'slug' : (activeTab === 'routes' ? 'path' : 'name'))}
                                </th>
                                {activeTab === 'groups' && <th>{t('permissions')}</th>}
                                {activeTab === 'routes' && <th>{t('method')}</th>}
                                <th onClick={() => handleSort('createdAt')} className="sortable">
                                    {t('date')} {renderSortArrow('createdAt')}
                                </th>
                                <th>{t('actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pagination[activeTab].items.map(item => (
                                <tr
                                    key={item.id}
                                    className={selectedItem?.id === item.id ? 'active' : ''}
                                    onClick={() => handleSelectItem(item)}
                                >
                                    <td>
                                        <div className="item-title">
                                            <strong>{item.name || item.slug || item.path}</strong>
                                            <span className="item-desc">{item.description || t('no_description')}</span>
                                        </div>
                                    </td>
                                    {activeTab === 'groups' && (
                                        <td><span className="count-badge">{t('perms_count', { count: item.permissions?.length || 0 })}</span></td>
                                    )}
                                    {activeTab === 'routes' && (
                                        <td>
                                            <span className={`method-badge ${item.method?.toLowerCase()}`}>{item.method}</span>
                                            {item.is_public && <span className="public-badge" title="Access for all (Public)">🔓</span>}
                                        </td>
                                    )}
                                    <td>
                                        <span className="date-cell">{new Date(item.createdAt).toLocaleDateString()}</span>
                                    </td>
                                    <td><button className="btn-details">{t('details')} →</button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="list-footer">
                    <div className="pagination-info">
                        {t('page_of', { current: pagination[activeTab].currentPage, total: pagination[activeTab].pages })}
                    </div>
                    <div className="pagination-btns">
                        <button disabled={pagination[activeTab].currentPage === 1} onClick={() => loadTabData(activeTab, pagination[activeTab].currentPage - 1)}>{t('previous_page')}</button>
                        <button disabled={pagination[activeTab].currentPage === pagination[activeTab].pages} onClick={() => loadTabData(activeTab, pagination[activeTab].currentPage + 1)}>{t('next_page')}</button>
                    </div>
                </div>
            </div>

            <div className="detail-panel">
                {(selectedItem || showCreateForm) ? (
                    <div className="detail-container">
                        <div className="detail-header">
                            <h3>{showCreateForm ? t('new_item_title', { item: t(activeTab.slice(0, -1)) }) : t('edit_item', { item: t(activeTab.slice(0, -1)) })}</h3>
                            {!showCreateForm && <button className="btn-delete" onClick={handleDelete}>{t('cancel')}</button>}
                        </div>

                        <form className="detail-form" onSubmit={handleSave}>
                            {activeTab === 'groups' && (
                                <>
                                    <div className="form-group">
                                        <label>{t('group_name')}</label>
                                        <input
                                            type="text" value={editForm.group.name}
                                            onChange={e => setEditForm({ ...editForm, group: { ...editForm.group, name: e.target.value } })}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Description</label>
                                        <textarea
                                            value={editForm.group.description}
                                            onChange={e => setEditForm({ ...editForm, group: { ...editForm.group, description: e.target.value } })}
                                        />
                                    </div>
                                    <div className="selector-container">
                                        <VisualSelector
                                            title={t('groups') + " " + t('permissions')}
                                            items={allPermissions}
                                            selectedIds={editForm.group.permissionIds}
                                            onToggle={id => {
                                                const ids = editForm.group.permissionIds;
                                                const next = ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id];
                                                setEditForm({ ...editForm, group: { ...editForm.group, permissionIds: next } });
                                            }}
                                        />
                                    </div>
                                    <div className="selectors-stack">
                                        <div className="selector-wrapper">
                                            <VisualSelector
                                                title={t('roles') + " (Default)"}
                                                subtitle={t('automatically_assigned')}
                                                items={allRoles}
                                                selectedIds={editForm.group.roles.filter(r => r.isDefault).map(r => r.roleId)}
                                                onToggle={id => {
                                                    const isSelected = editForm.group.roles.some(r => r.roleId === id && r.isDefault);
                                                    let nextRoles;
                                                    if (isSelected) {
                                                        nextRoles = editForm.group.roles.filter(r => r.roleId !== id);
                                                    } else {
                                                        nextRoles = [
                                                            ...editForm.group.roles.filter(r => r.roleId !== id),
                                                            { roleId: id, isDefault: true }
                                                        ];
                                                    }
                                                    setEditForm({ ...editForm, group: { ...editForm.group, roles: nextRoles } });
                                                }}
                                            />
                                        </div>
                                        <div className="selector-wrapper">
                                            <VisualSelector
                                                title={t('roles') + " (Optional)"}
                                                subtitle={t('available_selection')}
                                                items={allRoles}
                                                selectedIds={editForm.group.roles.filter(r => !r.isDefault).map(r => r.roleId)}
                                                onToggle={id => {
                                                    const isSelected = editForm.group.roles.some(r => r.roleId === id && !r.isDefault);
                                                    let nextRoles;
                                                    if (isSelected) {
                                                        nextRoles = editForm.group.roles.filter(r => r.roleId !== id);
                                                    } else {
                                                        nextRoles = [
                                                            ...editForm.group.roles.filter(r => r.roleId !== id),
                                                            { roleId: id, isDefault: false }
                                                        ];
                                                    }
                                                    setEditForm({ ...editForm, group: { ...editForm.group, roles: nextRoles } });
                                                }}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            {activeTab === 'permissions' && (
                                <>
                                    <div className="form-group">
                                        <label>{t('permission_slug')}</label>
                                        <input
                                            type="text" value={editForm.permission.slug}
                                            onChange={e => setEditForm({ ...editForm, permission: { ...editForm.permission, slug: e.target.value } })}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Description</label>
                                        <input
                                            type="text" value={editForm.permission.description}
                                            onChange={e => setEditForm({ ...editForm, permission: { ...editForm.permission, description: e.target.value } })}
                                        />
                                    </div>
                                </>
                            )}

                            {activeTab === 'roles' && (
                                <>
                                    <div className="form-group">
                                        <label>{t('role_name')}</label>
                                        <input
                                            type="text" value={editForm.role.name}
                                            onChange={e => setEditForm({ ...editForm, role: { ...editForm.role, name: e.target.value } })}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Description</label>
                                        <textarea
                                            value={editForm.role.description}
                                            onChange={e => setEditForm({ ...editForm, role: { ...editForm.role, description: e.target.value } })}
                                        />
                                    </div>

                                    <div className="selectors-stack">
                                        <div className="selector-wrapper">
                                            <VisualSelector
                                                title={t('default_groups')}
                                                subtitle={t('automatically_assigned')}
                                                items={allGroups}
                                                selectedIds={editForm.role.groups.filter(g => g.isDefault).map(g => g.groupId)}
                                                onToggle={id => {
                                                    const isSelected = editForm.role.groups.some(g => g.groupId === id && g.isDefault);
                                                    let nextGroups;
                                                    if (isSelected) {
                                                        nextGroups = editForm.role.groups.filter(g => g.groupId !== id);
                                                    } else {
                                                        // Add to default, remove from optional if exists
                                                        nextGroups = [
                                                            ...editForm.role.groups.filter(g => g.groupId !== id),
                                                            { groupId: id, isDefault: true }
                                                        ];
                                                    }
                                                    setEditForm({ ...editForm, role: { ...editForm.role, groups: nextGroups } });
                                                }}
                                            />
                                        </div>

                                        <div className="selector-wrapper">
                                            <VisualSelector
                                                title={t('optional_groups')}
                                                subtitle={t('available_selection')}
                                                items={allGroups}
                                                selectedIds={editForm.role.groups.filter(g => !g.isDefault).map(g => g.groupId)}
                                                onToggle={id => {
                                                    const isSelected = editForm.role.groups.some(g => g.groupId === id && !g.isDefault);
                                                    let nextGroups;
                                                    if (isSelected) {
                                                        nextGroups = editForm.role.groups.filter(g => g.groupId !== id);
                                                    } else {
                                                        // Add to optional, remove from default if exists
                                                        nextGroups = [
                                                            ...editForm.role.groups.filter(g => g.groupId !== id),
                                                            { groupId: id, isDefault: false }
                                                        ];
                                                    }
                                                    setEditForm({ ...editForm, role: { ...editForm.role, groups: nextGroups } });
                                                }}
                                            />
                                        </div>

                                        <div className="selector-wrapper">
                                            <VisualSelector
                                                title={t('direct_permission_overrides')}
                                                subtitle="Specific to this role"
                                                items={allPermissions.map(p => ({ id: p.id, name: p.slug, description: p.description }))}
                                                selectedIds={editForm.role.directPermissionIds}
                                                onToggle={id => {
                                                    const ids = editForm.role.directPermissionIds;
                                                    const next = ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id];
                                                    setEditForm({ ...editForm, role: { ...editForm.role, directPermissionIds: next } });
                                                }}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            {activeTab === 'routes' && (
                                <>
                                    <div className="form-group-row">
                                        <div className="form-group" style={{ flex: 2 }}>
                                            <label>{t('route_path')}</label>
                                            <input
                                                type="text" value={editForm.route.path}
                                                onChange={e => setEditForm({ ...editForm, route: { ...editForm.route, path: e.target.value } })}
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>{t('method')}</label>
                                            <select
                                                value={editForm.route.method}
                                                onChange={e => setEditForm({ ...editForm, route: { ...editForm.route, method: e.target.value } })}
                                            >
                                                <option value="ALL">ALL</option>
                                                <option value="GET">GET</option>
                                                <option value="POST">POST</option>
                                                <option value="PUT">PUT</option>
                                                <option value="DELETE">DELETE</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="checkbox-label">
                                            <input
                                                type="checkbox"
                                                checked={editForm.route.is_public}
                                                onChange={e => setEditForm({ ...editForm, route: { ...editForm.route, is_public: e.target.checked } })}
                                            />
                                            <span className="checkbox-text">🌎 {t('public_route', 'Rota Pública')} (Sem autenticação)</span>
                                        </label>
                                    </div>
                                    <div className="selector-container">
                                        <VisualSelector
                                            title="Required Permissions (Slugs)"
                                            items={allPermissions}
                                            selectedIds={editForm.route.permissions.map(slug => allPermissions.find(p => p.slug === slug)?.id).filter(Boolean)}
                                            onToggle={id => {
                                                const slug = allPermissions.find(p => p.id === id).slug;
                                                const current = editForm.route.permissions;
                                                const next = current.includes(slug) ? current.filter(s => s !== slug) : [...current, slug];
                                                setEditForm({ ...editForm, route: { ...editForm.route, permissions: next } });
                                            }}
                                        />
                                    </div>
                                </>
                            )}

                            <div className="form-footer-actions">
                                <button type="submit" className="btn-save">{t('save_changes')}</button>
                                <button type="button" className="btn-cancel" onClick={() => { setSelectedItem(null); setShowCreateForm(false); }}>{t('discard')}</button>
                            </div>
                        </form>
                    </div>
                ) : (
                    <div className="empty-detail-state">
                        <div className="icon">📋</div>
                        <p>Select an item to view details or click <strong>+ New</strong> to create one.</p>
                    </div>
                )}
            </div>

            <ConfirmationModal
                isOpen={confirmModal.isOpen}
                title={t('delete_confirm_msg', { item: t(activeTab.slice(0, -1)) })}
                message={t('delete_confirm_msg', { item: t(activeTab.slice(0, -1)) })}
                onConfirm={confirmDeleteAction}
                onCancel={() => setConfirmModal({ isOpen: false })}
            />
        </div>
    );
};

export default AdminAccessControl;
